import { ModulePermission } from 'api/client-domain/client-domain.types';
import { FeatureFlag } from './client-configuration';
import { IncentivioRouteObject } from 'pages/main-routes';
import { getStatsigGateValue } from 'util/statsig';

type AccessAtom<T = string> = {
  name: T;
  negate?: boolean;
};

type AccessObject = {
  permission?: AccessAtom;
  statsigFeatureGate?: AccessAtom;
  featureFlag?: AccessAtom<FeatureFlag>;
};

export type AccessDefinition = {
  any?: AccessObject[];
  all?: AccessObject[];
  one?: AccessObject;
};

export const checkPermission = (
  accessAtom: AccessAtom,
  allPermissions: ModulePermission[],
): boolean =>
  (!accessAtom
    ? true
    : allPermissions?.some(
        permissionObject => permissionObject.permission === accessAtom.name,
      )) === !accessAtom?.negate;

export const checkStatsigFeatureGate = (accessAtom: AccessAtom): boolean =>
  (!accessAtom ? true : getStatsigGateValue(accessAtom.name)) ===
  !accessAtom?.negate;

export const checkFeatureFlag = (
  accessAtom: AccessAtom<FeatureFlag>,
  allFeatureFlags: { [key in FeatureFlag]?: boolean },
): boolean =>
  (!accessAtom ? true : allFeatureFlags?.[accessAtom.name]) ===
  !accessAtom?.negate;

export const checkAccess = (
  accessObject: AccessObject,
  allPermissions: ModulePermission[],
  allFeatureFlags: { [key in FeatureFlag]?: boolean },
): boolean => {
  if (!accessObject) return true;

  const permissionCheck = checkPermission(
    accessObject.permission,
    allPermissions,
  );
  const statsigFeatureGateCheck = checkStatsigFeatureGate(
    accessObject.statsigFeatureGate,
  );
  const featureFlagCheck = checkFeatureFlag(
    accessObject.featureFlag,
    allFeatureFlags,
  );

  return permissionCheck && statsigFeatureGateCheck && featureFlagCheck;
};

export const checkAnyAccess = (
  any: AccessObject[],
  allPermissions: ModulePermission[],
  allFeatureFlags: { [key in FeatureFlag]?: boolean },
): boolean => {
  if (!any) return true;
  return any.some(accessObject =>
    checkAccess(accessObject, allPermissions, allFeatureFlags),
  );
};

export const checkAllAccess = (
  all: AccessObject[],
  allPermissions: ModulePermission[],
  allFeatureFlags: { [key in FeatureFlag]?: boolean },
): boolean => {
  if (!all) return true;
  return all.every(accessObject =>
    checkAccess(accessObject, allPermissions, allFeatureFlags),
  );
};

export const checkAccessDefinition = (
  accessDefinition: AccessDefinition,
  allPermissions: ModulePermission[],
  allFeatureFlags: { [key in FeatureFlag]?: boolean },
): boolean => {
  if (!accessDefinition) return true;

  const anyCheck = checkAnyAccess(
    accessDefinition.any,
    allPermissions,
    allFeatureFlags,
  );
  const allCheck = checkAllAccess(
    accessDefinition.all,
    allPermissions,
    allFeatureFlags,
  );
  const oneCheck = checkAccess(
    accessDefinition.one,
    allPermissions,
    allFeatureFlags,
  );

  return anyCheck && allCheck && oneCheck;
};

export const pruneRoutes = (
  routes: IncentivioRouteObject[],
  allPermissions: ModulePermission[],
  allFeatureFlags: { [key in FeatureFlag]?: boolean },
) => {
  const newRoutes = routes.reduce((result, route) => {
    if (
      !route.access ||
      checkAccessDefinition(route.access, allPermissions, allFeatureFlags)
    )
      result.push({ ...route });
    return result;
  }, []);

  newRoutes.forEach(route => {
    if (!!route.children)
      route.children = pruneRoutes(
        route.children,
        allPermissions,
        allFeatureFlags,
      );
  });

  return newRoutes;
};
